import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { addingSideEffects, deletingSideEffects, errorSideEffects, loadingSideEffects, successSideEffects, updatingSideEffects } from "..";
import { Toastify } from "../../../components/Toaster";

interface User {
  Id: string;
  UserId: string;
  Roles: string;
  OrgId: string;
  Email: string;
  CreatedDate: Date;
  ModifiedDate: Date;
}

interface Invitation {
  Id: string;
  OrgId: string;
  RoleIds: string[];
  Status: string;
  EmailId: string;
  CreatedDate: string;
  ExpirationDate: string;
  ModifiedDate: string;
  InviterUid: string;
}

export interface OrganizationUsersState {
  Data: User[];
  TotalCount: number;
  isLoading: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isAdding: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage: string | null;
}

export interface InviteUsersState {
  Data: Invitation[];
  isLoading: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isAdding: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage: string | null;
}

interface UserAPIResponse {
  Data: User[];
  TotalCount: number;
  ErrorMessage?: string;
  ErrorCode?: number;
}

interface UserInvitationAPIResponse {
  Data: Invitation[];
  ErrorMessage?: string;
  ErrorCode?: number;
}

const initialInvitationState: InviteUsersState = {
  Data: [],
  isLoading: false,
  isUpdating: false,
  isDeleting: false,
  isAdding: false,
  isSuccess: false,
  isError: false,
  errorMessage: null
};

const initialState: OrganizationUsersState = {
  Data: [],
  TotalCount: 0,
  isLoading: false,
  isUpdating: false,
  isDeleting: false,
  isAdding: false,
  isSuccess: false,
  isError: false,
  errorMessage: null
};

export const OrganizationUsersSlice = createSlice({
  name: "organizationUsers",
  initialState,
  reducers: {
    getOrgUsersRequest(state, _) {
      Object.assign(state, loadingSideEffects);
    },
    getOrgUsersSuccess(state, action: PayloadAction<UserAPIResponse>) {
      Object.assign(state, successSideEffects);
      const sortedData = action.payload.Data.sort((a, b) => {
        return new Date(b.CreatedDate).getTime() - new Date(a.CreatedDate).getTime();
      });
      state.Data = sortedData;
      state.TotalCount = action.payload.TotalCount;
    },
    getOrgUsersFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects);
      state.errorMessage = action.payload;
    },
    updateOrgUsersRequest(state, _: PayloadAction<any>) {
      Object.assign(state, updatingSideEffects);
    },
    updateOrgUsersSuccess(state, action: PayloadAction<any>) {
      Object.assign(state, successSideEffects);
      const failedUsersCount = action.payload.reduce((count, user) => count + (user.ErrorCode ? 1 : 0), 0);
      if (failedUsersCount > 0) {
        Toastify("error", `${failedUsersCount} user(s) out of ${action.payload.length} user(s) failed to update`);
      } else Toastify("success", "Users updated successfully");
      // const index = state.Data.findIndex((user) => user.Id === action.payload.Id);
      // if (index !== -1) {
      //   state.Data[index] = { ...state.Data[index], ...action.payload };
      // }
    },
    updateOrgUsersFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },

    deleteOrgUsersRequest(state, _: PayloadAction<any>) {
      Object.assign(state, deletingSideEffects);
    },
    deleteOrgUsersSuccess(state, action: PayloadAction<{ userId: string }>) {
      Object.assign(state, successSideEffects);
      state.isDeleting = false;
      state.isSuccess = true;
      state.Data = state.Data.filter((user: User) => user.UserId !== action.payload.userId);
      Toastify("success", "User removed from Organization successfully");
    },
    deleteOrgUsersFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects);
      state.errorMessage = action.payload;
    }

    // addUser(state, action: PayloadAction<User>) {
    //   state.users.push(action.payload);
    // },
    // removeUser(state, action: PayloadAction<string>) {
    //   state.users = state.users.filter(user => user.id !== action.payload);
    // },
    // updateUser(state, action: PayloadAction<User>) {
    //   const index = state.users.findIndex(user => user.id === action.payload.id);
    //   if (index !== -1) {
    //     state.users[index] = action.payload;
    //   }
    // },
  }
});

export const UserInvitationsSlice = createSlice({
  name: "userInvitations",
  initialState: initialInvitationState,
  reducers: {
    getAllUserInvitationsRequest(state, _) {
      Object.assign(state, loadingSideEffects);
    },
    getAllUserInvitationsSuccess(state, action: PayloadAction<UserInvitationAPIResponse>) {
      Object.assign(state, successSideEffects);
      state.Data = action.payload.Data;
    },
    getAllUserInvitationsFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects);
      state.errorMessage = action.payload;
    },
    // getUserInvitationsRequest(state, _) {
    //   Object.assign(state, loadingSideEffects);
    // },
    // getUserInvitationsSuccess(state, action: PayloadAction<UserAPIResponse>) {
    //   Object.assign(state, successSideEffects);

    //   state.Data = action.payload.Data;
    // },
    // getUserInvitationsFailure(state, action: PayloadAction<string>) {
    //   Object.assign(state, errorSideEffects);
    //   state.errorMessage = action.payload;
    // },
    // updateUserInvitationsRequest(state, _) {
    //   Object.assign(state, loadingSideEffects);
    // },
    // updateUserInvitationsSuccess(state, action: PayloadAction<User>) {
    //   Object.assign(state, successSideEffects);
    //   const index = state.Data.findIndex((user) => user.Id === action.payload.Id);
    //   if (index !== -1) {
    //     state.Data[index] = { ...state.Data[index], ...action.payload };
    //   }
    // },
    // updateUserInvitationsFailure(state, action: PayloadAction<string>) {
    //   Object.assign(state, errorSideEffects);
    //   state.errorMessage = action.payload;
    // },
    createUserInvitationRequest(state, _: PayloadAction<any>) {
      Object.assign(state, addingSideEffects);
    },
    createUserInvitationSuccess(state, action: PayloadAction<Invitation>) {
      Object.assign(state, successSideEffects);
      state.Data.push(action.payload);
      Toastify("success", "Invitation sent successfully");
    },
    createUserInvitationFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    deleteUserInvitationRequest(state, _: PayloadAction<any>) {
      Object.assign(state, deletingSideEffects);
    },
    deleteUserInvitationSuccess(state, action: PayloadAction<Invitation>) {
      Object.assign(state, successSideEffects);
      const index = state.Data.findIndex((user) => user.Id === action.payload.Id);
      if (index !== -1) {
        state.Data[index].Status = "Revoked";
      }
      Toastify("success", "Invitation revoked successfully");
    },
    deleteUserInvitationFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    resendUserInvitationRequest(state, _: PayloadAction<any>) {
      Object.assign(state, updatingSideEffects);
    },
    resendUserInvitationSuccess(state, action: PayloadAction<Invitation>) {
      Object.assign(state, successSideEffects);
      const index = state.Data.findIndex((user) => user.Id === action.payload.Id);
      if (index !== -1) {
        state.Data[index] = action.payload;
      }
      Toastify("success", "Invitation resent successfully");
    },
    resendUserInvitationFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    }
  }
});

export const {
  getAllUserInvitationsRequest,
  getAllUserInvitationsSuccess,
  getAllUserInvitationsFailure,
  // getUserInvitationsRequest,
  // getUserInvitationsSuccess,
  // getUserInvitationsFailure,
  // updateUserInvitationsRequest,
  // updateUserInvitationsSuccess,
  // updateUserInvitationsFailure,
  createUserInvitationRequest,
  createUserInvitationSuccess,
  createUserInvitationFailure,
  deleteUserInvitationRequest,
  deleteUserInvitationSuccess,
  deleteUserInvitationFailure,
  resendUserInvitationRequest,
  resendUserInvitationSuccess,
  resendUserInvitationFailure
} = UserInvitationsSlice.actions;

export const {
  getOrgUsersRequest,
  getOrgUsersSuccess,
  getOrgUsersFailure,
  updateOrgUsersRequest,
  updateOrgUsersSuccess,
  updateOrgUsersFailure,
  deleteOrgUsersRequest,
  deleteOrgUsersSuccess,
  deleteOrgUsersFailure

  // addUser,
  // removeUser,
  // updateUser,
} = OrganizationUsersSlice.actions;
